/**
 * useSalespersonData Hook
 *
 * Fetches per-salesperson performance data from /salesperson-performance.
 * Refresh delegates to refreshSalesManagerData — the underlying loader writes
 * both MANAGER# and SALESPERSON# records in the same DynamoDB partition.
 *
 * The endpoint also returns the same people bucketed into sales teams
 * (Configuration → Sales Teams) with per-team subtotals; the active column
 * sort is applied within each team, matching the Sales Funnel card. `teams` is
 * empty only when the backend predates team grouping — the card then renders
 * the flat ranked list.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getSalespersonPerformance,
  refreshSalesManagerData,
  SalespersonPerformanceResponse,
} from '../services/api';
import {
  Salesperson,
  SalespersonSummary,
  SalespersonTeam,
  SortConfig,
} from '../components/forecast/SalespersonPerformance/types';

interface UseSalespersonDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean;
}

interface UseSalespersonDataResult {
  salespeople: Salesperson[];
  /** Same people grouped by sales team, sorted by team name (Unassigned last). */
  teams: SalespersonTeam[];
  summary: SalespersonSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  refresh: () => Promise<void>;
}

function transformSalesperson(raw: SalespersonPerformanceResponse['salespeople'][0]): Salesperson {
  return {
    salespersonId: raw.salespersonId,
    salespersonName: raw.salespersonName,
    managerId: raw.managerId,
    locationId: raw.locationId,
    year: raw.year,
    month: raw.month,
    totalUnits: raw.totalUnits,
    newUnits: raw.newUnits,
    usedUnits: raw.usedUnits,
    swapUnits: raw.swapUnits ?? 0,
    wholesaleUnits: raw.wholesaleUnits ?? 0,
    frontGross: raw.frontGross,
    fiGross: raw.fiGross,
    holdback: raw.holdback,
    totalGross: raw.totalGross,
    avgFrontGrossPerUnit: raw.avgFrontGrossPerUnit,
    avgFiGrossPerUnit: raw.avgFiGrossPerUnit,
    avgHoldbackPerUnit: raw.avgHoldbackPerUnit,
    avgTotalGrossPerUnit: raw.avgTotalGrossPerUnit,
  };
}

function transformTeams(
  raw: SalespersonPerformanceResponse['teams']
): SalespersonTeam[] {
  if (!raw) return [];
  return raw.map((team) => ({
    managerId: team.managerId,
    managerName: team.managerName,
    salespeople: team.salespeople.map(transformSalesperson),
    subtotals: team.subtotals,
  }));
}

function transformSummary(
  raw: SalespersonPerformanceResponse['summary'],
  locationId: number,
  year: number,
  month: number
): SalespersonSummary | null {
  if (!raw) return null;

  return {
    locationId,
    year,
    month,
    managerCount: raw.managerCount,
    totalGross: raw.totalGross,
    totalFrontGross: raw.totalFrontGross,
    totalFiGross: raw.totalFiGross,
    totalHoldback: raw.totalHoldback,
    totalUnits: raw.totalUnits,
    totalNewUnits: raw.totalNewUnits,
    totalUsedUnits: raw.totalUsedUnits,
    totalSwapUnits: raw.totalSwapUnits ?? 0,
    totalWholesaleUnits: raw.totalWholesaleUnits ?? 0,
    lastUpdated: raw.lastUpdated,
  };
}

export function useSalespersonData({
  locationId,
  year,
  month,
  enabled,
}: UseSalespersonDataParams): UseSalespersonDataResult {
  const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
  const [teams, setTeams] = useState<SalespersonTeam[]>([]);
  const [summary, setSummary] = useState<SalespersonSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'totalGross',
    direction: 'desc',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSalespersonPerformance(locationId, year, month);

      if (response.success && response.data) {
        const transformed = response.data.salespeople.map(transformSalesperson);
        setSalespeople(transformed);
        setTeams(transformTeams(response.data.teams));
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load salesperson data');
        setSalespeople([]);
        setTeams([]);
        setSummary(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setSalespeople([]);
      setTeams([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setSalespeople([]);
      setTeams([]);
      setSummary(null);
      setError(null);
      return;
    }

    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const refresh = useCallback(async () => {
    if (!enabled || locationId === 0) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshResponse = await refreshSalesManagerData(locationId, year, month);
      if (!refreshResponse.success) {
        setError(refreshResponse.error || 'Failed to refresh salesperson data');
        return;
      }

      const response = await getSalespersonPerformance(locationId, year, month);
      if (response.success && response.data) {
        const transformed = response.data.salespeople.map(transformSalesperson);
        setSalespeople(transformed);
        setTeams(transformTeams(response.data.teams));
        setSummary(transformSummary(response.data.summary, locationId, year, month));
      } else {
        setError(response.error || 'Failed to load updated salesperson data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during refresh');
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, enabled]);

  const compareSalespeople = useCallback(
    (a: Salesperson, b: Salesperson) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      if (field === 'name') {
        comparison = a.salespersonName.localeCompare(b.salespersonName);
      } else {
        const aVal = a[field] ?? 0;
        const bVal = b[field] ?? 0;
        comparison = aVal - bVal;
      }

      return direction === 'asc' ? comparison : -comparison;
    },
    [sortConfig]
  );

  const sortedSalespeople = useMemo(
    () => (salespeople.length ? [...salespeople].sort(compareSalespeople) : []),
    [salespeople, compareSalespeople]
  );

  // Team order is fixed by the backend (name, Unassigned last); only members
  // re-order, so the card's sort applies within each team block.
  const sortedTeams = useMemo(
    () => teams.map((team) => ({
      ...team,
      salespeople: [...team.salespeople].sort(compareSalespeople),
    })),
    [teams, compareSalespeople]
  );

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return {
    salespeople: sortedSalespeople,
    teams: sortedTeams,
    summary,
    loading,
    refreshing,
    error,
    sortConfig,
    setSortConfig: handleSortChange,
    refresh,
  };
}
